// The call ends when Twilio's participant duration limit is reached; Twilio gives no warning. video-api
// sends how many seconds are left with the token response, and this keeps the moment that runs out so a
// countdown can be shown for the last few minutes. Working from a stored end time, not a running timer,
// means a phone's throttled background tab still shows the right time when it wakes.

/** The countdown appears when this many seconds are left. */
export const WARNING_SECONDS = 5 * 60;

let endsAtMs: number | null = null;

/** Called once, when the token response arrives. Anything that is not a number of seconds clears it (an older video-api). */
export function setCallSecondsRemaining(seconds: unknown, now: number = Date.now()) {
  endsAtMs = typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0 ? now + seconds * 1000 : null;
}

/** Whole seconds left, rounded up so the clock reaches 0:00 as the call ends; null when it is not known. */
export function callSecondsLeft(now: number = Date.now()): number | null {
  if (endsAtMs === null) return null;
  return Math.max(0, Math.ceil((endsAtMs - now) / 1000));
}

/** m:ss, for example 4:32. */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const COUNTDOWN_KEY = 'vuiCountdown';

/**
 * For trying the countdown without a real call that runs for an hour. Open any page of the dev video-ui with
 * ?countdown=320 and, from then on in that browser, calls behave as though 320 seconds were left (so the host
 * window that Messenger opens counts down too). ?countdown=off turns it off again. It never applies on the
 * production host. Returns the number of seconds to pretend, or null when there is no override.
 *
 * Only a person's own browser is affected, and Twilio's limit is enforced on the server whatever this page shows.
 */
export function countdownOverride(
  search: string,
  hostname: string,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null,
): number | null {
  if (hostname.includes('prod3-vod-ui')) return null;
  const param = new URLSearchParams(search).get('countdown');
  try {
    if (param === 'off') {
      storage?.removeItem(COUNTDOWN_KEY);
      return null;
    }
    if (param !== null && /^\d{1,5}$/.test(param)) {
      storage?.setItem(COUNTDOWN_KEY, param);
      return Number(param);
    }
    const kept = storage?.getItem(COUNTDOWN_KEY);
    return kept && /^\d{1,5}$/.test(kept) ? Number(kept) : null;
  } catch (e) {
    // Storage can be blocked (private browsing): the value on the URL still works for this page.
    return param !== null && /^\d{1,5}$/.test(param) ? Number(param) : null;
  }
}

/**
 * The seconds to count down from. Normally what video-api sent. With a test override (pretend the call has
 * `override` seconds left from its start) a page that loads late starts partway through the countdown, so host
 * and guest still agree on when the call ends, as they do in a real call. Without the server's numbers the
 * override is simply counted from now.
 */
export function effectiveSecondsRemaining(override: number | null, max: unknown, remaining: unknown): unknown {
  if (override === null) return remaining;
  if (typeof max === 'number' && typeof remaining === 'number' && Number.isFinite(max) && Number.isFinite(remaining)) {
    const elapsed = Math.max(0, max - remaining);
    return Math.max(0, override - elapsed);
  }
  return override;
}
